'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchAllRows } from '@kit/shared/lib/fetch-all-rows';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import {
  isConversationInScope,
  PERSONAL_INBOX_SCOPE,
  type InboxScope,
} from '../lib/org-conversation';
import type { ConversationWithDetails } from '../types';
import { useCurrentUser } from './use-current-user';

/**
 * Raw conversation data from database query
 */
type ConversationQueryRow = Omit<ConversationWithDetails, 'my_participation'>;

interface UseConversationsOptions {
  realtime?: boolean;
}

/**
 * Hook to fetch and subscribe to user conversations
 */
export function useConversations(options: UseConversationsOptions = {}) {
  const { realtime = true } = options;
  const client = useSupabase();
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof client.channel> | null>(null);
  const { userId } = useCurrentUser();

  const queryKey = useMemo(() => ['chat-conversations'], []);

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<ConversationWithDetails[]> => {
      // Use cached userId instead of calling getUser()
      if (!userId) {
        return [];
      }

      const { data, error } = await client
        .from('conversations')
        .select(
          `
          *,
          participants:conversation_participants(
            *,
            profile:profiles!conversation_participants_user_id_profiles_fkey(id, first_name, last_name, profile_photo_url, phone_number, is_deleted, deleted_at, auth_user_id, is_merged)
          ),
          organization:organizations!conversations_organization_id_fkey(id, name, slug, logo_media_id),
          latest_message:messages(metadata)
        `,
        )
        .is('deleted_at', null)
        // Embed shape: project ONLY the latest message's metadata so
        // ConversationList can branch on `metadata.intent_type` without
        // a second round-trip. The order+limit apply to the embedded
        // `messages` rows (not the parent conversations rows).
        .order('created_at', {
          ascending: false,
          referencedTable: 'messages',
        })
        .limit(1, { referencedTable: 'messages' })
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (error) {
        throw error;
      }

      // Transform to ConversationWithDetails
      return (data || []).map((conv) => {
        const row = conv as unknown as ConversationQueryRow;
        const participants = row.participants || [];
        return {
          ...row,
          participants,
          my_participation:
            participants.find((p) => p.user_id === userId) || null,
        };
      });
    },
    enabled: !!userId, // Only run when user is loaded
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 30000,
  });

  // Set up realtime subscription for conversation updates
  useEffect(() => {
    if (!realtime || !userId) {
      return;
    }

    // Clean up existing subscription
    if (channelRef.current) {
      client.removeChannel(channelRef.current);
    }

    // Subscribe to conversation updates
    const channel = client
      .channel('chat-conversations')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_participants',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        client.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [client, realtime, queryClient, queryKey, userId]);

  return query;
}

/**
 * Hook to get a single conversation by ID
 */
export function useConversation(conversationId: string | null) {
  const client = useSupabase();
  const { userId } = useCurrentUser();

  return useQuery({
    queryKey: ['chat-conversation', conversationId],
    queryFn: async (): Promise<ConversationWithDetails | null> => {
      if (!conversationId || !userId) return null;

      const { data, error } = await client
        .from('conversations')
        .select(
          `
          *,
          participants:conversation_participants(
            *,
            profile:profiles!conversation_participants_user_id_profiles_fkey(id, first_name, last_name, profile_photo_url, phone_number, is_deleted, deleted_at, auth_user_id, is_merged)
          ),
          organization:organizations!conversations_organization_id_fkey(id, name, slug, logo_media_id)
        `,
        )
        .eq('id', conversationId)
        .is('deleted_at', null)
        .single();

      if (error) {
        throw error;
      }

      const row = data as unknown as ConversationQueryRow;
      const participants = row.participants || [];
      return {
        ...row,
        participants,
        my_participation:
          participants.find((p) => p.user_id === userId) || null,
      };
    },
    enabled: !!conversationId && !!userId,
    staleTime: 30000,
  });
}

/**
 * The organization ids the current user is a member of (BAL-580). Drives the
 * org-vs-personal inbox split and the "reply as org" view. One cached call to
 * `get_user_organization_ids` — the same membership table org-thread RLS reads.
 */
export function useMyOrganizationIds() {
  const client = useSupabase();
  const { userId } = useCurrentUser();

  return useQuery({
    queryKey: ['chat-my-organization-ids', userId],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await client.rpc('get_user_organization_ids');

      if (error) {
        throw error;
      }

      return data ?? [];
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to get the unread message count for one inbox (BAL-580: unread badges
 * are per workspace). Defaults to the personal inbox, which for a user with no
 * org is every conversation, exactly as before.
 */
export function useTotalUnreadCount(scope: InboxScope = PERSONAL_INBOX_SCOPE) {
  const client = useSupabase();
  const queryClient = useQueryClient();
  const { userId } = useCurrentUser();
  const { data: myOrganizationIds } = useMyOrganizationIds();

  const scopeKey =
    scope.kind === 'organization' ? scope.organizationId : 'personal';
  const queryKey = useMemo(
    () => ['chat-total-unread-count', scopeKey],
    [scopeKey],
  );

  const query = useQuery({
    queryKey: [...queryKey, myOrganizationIds],
    queryFn: async (): Promise<number> => {
      if (!userId) return 0;

      const { data, error } = await fetchAllRows((from, to) =>
        client
          .from('conversation_participants')
          .select(
            'unread_count, conversation:conversations!conversation_participants_conversation_id_fkey(organization_id)',
          )
          .eq('user_id', userId)
          .eq('is_active', true)
          .gt('unread_count', 0)
          .order('id')
          .range(from, to),
      );

      if (error) {
        throw error;
      }

      return (data || [])
        .filter((p) =>
          isConversationInScope(
            { organization_id: p.conversation?.organization_id ?? null },
            scope,
            myOrganizationIds ?? [],
          ),
        )
        .reduce((sum, p) => sum + (p.unread_count || 0), 0);
    },
    enabled: !!userId && myOrganizationIds !== undefined,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Invalidation helper
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return { ...query, invalidate };
}
